// Logica de la calculadora de estimaciones en horas

import { ref, computed } from "vue";
import { calculateAutoSupervisionHours, calculateSupervisionOnlyHours } from "../utils/budgetCalculations.js";

export function useBudgetCalculator() {
  // Reactive state variables
  const clientName = ref(""); // Stores the client's name
  const ownerName = ref(""); // Stores the owner's name (the person creating the estimate)
  const jiraTicket = ref(""); // Stores the JIRA ticket reference
  const intranetUrl = ref(""); // Stores the Intranet URL reference
  const selectedService = ref(null); // Stores the currently selected service from the dropdown
  const selectedItems = ref([]); // Stores the list of all selected services/items

  // Calculate base hours without supervision
  const calculateBaseHours = () => {
    return selectedItems.value.reduce((sum, item) => {
      // Skip supervision-only items in base calculation
      if (item.supervisionOnly) return sum;
      
      // Ensure we're working with valid numbers
      const hours = parseFloat(item.service.hours) || 0;
      const quantity = parseFloat(item.quantity) || 1;
      return sum + (hours * quantity);
    }, 0);
  };

  // Base hours as computed property
  const baseHours = computed(calculateBaseHours);

  // Computed property for supervision-only hours
  const supervisionOnlyHours = computed(() => {
    return calculateSupervisionOnlyHours(selectedItems.value);
  });

  // Computed property to calculate creative supervision hours
  const creativeSupervisionHours = computed(() => {
    // Only calculate supervision if there are items
    if (selectedItems.value.length === 0) return 0;
    
    // Use the new per-item calculation method
    return calculateAutoSupervisionHours(baseHours.value, selectedItems.value);
  });

  // Total supervision hours (creative supervision + supervision-only items)
  const totalSupervisionHours = computed(() => {
    const creative = creativeSupervisionHours.value;
    const supervisionOnly = supervisionOnlyHours.value;
    const total = creative + supervisionOnly;
    return Math.round(total * 10) / 10; // Round to 1 decimal place
  });

  // Total hours including supervision
  const totalHours = computed(() => {
    const base = baseHours.value;
    const supervisionOnly = supervisionOnlyHours.value;
    const supervision = creativeSupervisionHours.value;
    const total = base + supervisionOnly + supervision;
    return Math.round(total * 10) / 10; // Round to 1 decimal place
  });
  // Explanation:
  // - Creative supervision hours are calculated per item as (itemHours / 6.5) * 1.5
  // - Each calculation step is rounded to 2 decimal places for precision
  // - All item supervision hours are summed for the final total

  // Method to add a selected service to the list of items
  const addItem = (supervisionOnly = false) => {
    if (selectedService.value && selectedService.value.name && selectedService.value.hours !== undefined) {
      // Check if the selected service is already in the list with the same supervision status
      const existingItem = selectedItems.value.find(
        (item) => item.service.name === selectedService.value.name && 
                  item.supervisionOnly === supervisionOnly
      );
      if (!existingItem) {
        // If the service is not already in the list with this supervision status, add it
        const newItem = {
          service: { 
            ...selectedService.value,
            defaultHours: selectedService.value.hours, // Store the default hours
            hours: selectedService.value.hours, // Ensure hours start at default
            team: selectedService.value.selectedTeam || null // Store the team information
          },
          quantity: 1,
          totalHours: Math.round(selectedService.value.hours * 100) / 100, // Rounded
          supervisionOnly: supervisionOnly // Add supervision-only flag
        };
        selectedItems.value.push(newItem);
        return { success: true, message: 'Item agregado exitosamente' };
      } else {
        // Item already exists with this supervision status
        const statusText = supervisionOnly ? 'solo supervisión' : 'regular';
        const message = `"${selectedService.value.name}" como item ${statusText} ya existe en la lista`;
        console.warn(message);
        return { success: false, message: message };
      }
    } else {
      // No valid service selected
      return { success: false, message: 'Por favor selecciona un servicio válido' };
    }
  };

  // Method to add a custom item to the list of items
  const addCustomItem = (customItem, supervisionOnly = false) => {
    // Ensure we have valid numbers
    const hours = parseFloat(customItem.hours) || 0;
    const quantity = parseFloat(customItem.quantity) || 1;
    
    const newItem = {
      service: { 
        name: customItem.name, 
        hours: Math.max(0.1, Math.round(hours * 10) / 10),
        defaultHours: Math.max(0.1, Math.round(hours * 10) / 10), // Store the default hours
        isCustom: true, // Mark as custom item to preserve hours
        team: null // Custom items don't have a specific team
      },
      quantity: quantity,
      totalHours: Math.round((hours * quantity) * 10) / 10, // Calculate total hours
      supervisionOnly: supervisionOnly // Add supervision-only flag
    };
    
    selectedItems.value.push(newItem);
  };

  // Method to toggle supervision-only status for an item
  const toggleSupervisionOnly = (index) => {
    const item = selectedItems.value[index];
    if (item) {
      item.supervisionOnly = !item.supervisionOnly;
    }
  };

  // Method to update the total hours of an item when its quantity changes
  const updateTotalHours = (index) => {
    const item = selectedItems.value[index]; // Get the item at the specified index
    if (!item) return;
    
    // Round to one decimal place
    const hours = parseFloat(item.service.hours) || 0;
    const quantity = parseFloat(item.quantity) || 1;
    item.totalHours = Math.round((hours * quantity) * 10) / 10;
  };

  // Method to update item hours when manually changed
  const updateItemHours = (index) => {
    const item = selectedItems.value[index];
    if (!item) return;

    // Make sure hours is a valid number and at least 0.1
    const newHours = Math.max(0.1, Math.round(parseFloat(item.service.hours || 0) * 10) / 10);
    item.service.hours = newHours;
    
    // Update the item's total hours
    const quantity = parseFloat(item.quantity) || 1;
    item.totalHours = Math.round((newHours * quantity) * 10) / 10;
  };

  // Method to remove an item from the list
  const removeItem = (index) => {
    selectedItems.value.splice(index, 1); // Use splice to remove the item at the specified index
  };

  // Return all reactive variables, computed properties, and methods
  return {
    clientName, // Client's name
    ownerName, // Owner's name
    jiraTicket, // JIRA ticket reference
    intranetUrl, // Intranet URL reference
    selectedService, // Currently selected service
    selectedItems, // List of selected items
    baseHours, // Base hours without supervision
    supervisionOnlyHours, // Supervision-only hours
    totalSupervisionHours, // Total supervision hours (creative + supervision-only)
    totalHours, // Total hours of all selected items
    creativeSupervisionHours, // Creative supervision hours
    addItem, // Method to add a selected service
    addCustomItem, // Method to add a custom item
    updateTotalHours, // Method to update total hours of an item
    updateItemHours, // Method to update hours of an item
    toggleSupervisionOnly, // Method to toggle supervision-only status
    removeItem, // Method to remove an item
  };
}